import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { PageTitle } from "../../components/Layout";
import { Modal, Field } from "../../components/ui-modal";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import { useConfirm } from "../../components/ConfirmDialog";

export default function AdminModules() {
  const { confirm } = useConfirm();
  const nav = useNavigate();
  const [modules, setModules] = useState([]);
  const [view, setView] = useState("projets"); // "projets" | "missions"
  const [editing, setEditing] = useState(null); // null | {} (new) | module (edit)
  const [form, setForm] = useState({ label: "", icon: "LayoutGrid" });

  const load = () => api.get("/admin/modules").then((r) => setModules(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ label: "", icon: "LayoutGrid" }); setEditing({}); };
  const openEdit = (m) => { setForm({ label: m.label, icon: m.icon || "LayoutGrid" }); setEditing(m); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.label.trim()) return toast.error("Donnez un nom au module");
    try {
      if (editing.id) { await api.put(`/admin/modules/${editing.id}`, form); toast.success("Module mis à jour"); }
      else { await api.post("/admin/modules", form); toast.success("Module créé"); }
      setEditing(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur"); }
  };

  const remove = async (m) => {
    if (!(await confirm({ title: `Supprimer le module « ${m.label} » ?`, confirmLabel: "Supprimer", tone: "danger" }))) return;
    try { await api.delete(`/admin/modules/${m.id}`); toast.success("Module supprimé"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Suppression impossible"); }
  };

  const isMissions = view === "missions";
  const openModule = (m) => nav(isMissions ? `/admin/modules/${m.key}/missions` : `/admin/modules/${m.key}`);

  return (
    <div>
      <PageTitle overline="Organisation" title="Modules"
        action={<button onClick={openNew} data-testid="new-module-btn" className="bg-marine text-white rounded-sm px-5 py-2.5 text-sm hover:bg-marine-hover flex items-center gap-2"><Icons.Plus size={16} /> Nouveau module</button>} />
      <p className="text-sm text-marine/80 mb-4 -mt-2">Vos prestations organisées par module. Choisissez une vue puis cliquez sur un module.</p>

      <div className="inline-flex items-center gap-1 bg-white border border-marine/15 rounded-sm p-1 mb-6" data-testid="module-view-toggle">
        <button onClick={() => setView("projets")} data-testid="module-view-projets"
          className={`px-4 py-1.5 rounded-sm text-sm transition-colors ${!isMissions ? "bg-marine text-white" : "text-marine hover:bg-marine/5"}`}>
          Projets
        </button>
        <button onClick={() => setView("missions")} data-testid="module-view-missions"
          className={`px-4 py-1.5 rounded-sm text-sm transition-colors ${isMissions ? "bg-marine text-white" : "text-marine hover:bg-marine/5"}`}>
          Missions
        </button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="modules-grid">
        {modules.map((m) => (
          <div key={m.id} data-testid={`module-card-${m.key}`}
            className="relative bg-sable rounded-sm border border-marine/10 shadow-sm p-5 card-lift hover:border-marine/40">
            <div className="absolute top-3 right-3 flex gap-1">
              <button aria-label={`Modifier le module ${m.label}`} onClick={() => openEdit(m)} data-testid={`edit-module-${m.key}`} className="p-1.5 rounded-sm text-marine hover:opacity-70 hover:bg-marine-50"><Icons.Pencil size={16} /></button>
              <button aria-label={`Supprimer le module ${m.label}`} onClick={() => remove(m)} data-testid={`delete-module-${m.key}`} className="p-1.5 rounded-sm text-marine hover:text-rouge hover:bg-rouge-50"><Icons.Trash2 size={16} /></button>
            </div>
            <button onClick={() => openModule(m)} data-testid={`open-module-${m.key}`} className="text-left w-full">
              <div className="text-xs uppercase tracking-wider text-bordeaux font-semibold mb-2">Module</div>
              <div className="font-display text-lg text-marine leading-tight mb-1 pr-14">{m.label}</div>
              {isMissions ? (
                <div className="text-sm text-marine/80" data-testid={`module-mission-count-${m.key}`}>{m.mission_count || 0} mission(s)</div>
              ) : (
                <div className="text-sm text-marine/80" data-testid={`module-project-count-${m.key}`}>{m.project_count || 0} projet(s)</div>
              )}
            </button>
          </div>
        ))}
        {modules.length === 0 && <p className="text-sm text-marine/80">Aucun module. Créez-en un pour organiser vos prestations.</p>}
      </div>

      {editing && (
        <Modal onClose={() => setEditing(null)} title={editing.id ? "Modifier le module" : "Nouveau module"}>
          <form onSubmit={save} className="space-y-4">
            <Field label="Nom du module"><input required value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="module-label" className="inp" placeholder="Ex : Publicité en ligne" /></Field>
            <div className="flex justify-end gap-2 pt-2"><button type="button" onClick={() => setEditing(null)} className="px-4 py-2 text-sm text-marine/80">Annuler</button><button data-testid="module-submit" className="bg-marine text-white rounded-sm px-5 py-2 text-sm">{editing.id ? "Enregistrer" : "Créer"}</button></div>
          </form>
        </Modal>
      )}
    </div>
  );
}
